import { RECRUITMENT_CHIP_HEIGHT, recruitmentChipStyle, recruitmentChipCountStyle, recruitmentChipRowStyle, recruitmentChipAccentStyle } from '@/components/domain/recruitment/recruitmentChipStyles';
import { UI_KIND, uiTargetAttrs } from '@/common/uiEventNames';
import { stationChip, recruitmentChips, accentChipOf, pinnedChipsOf, type StationClinic } from '@/components/domain/recruitment/recruitmentChips';
import { Link } from 'react-router-dom';
import { Grid } from '@mui/material';
import { RecruitmentGridProps, RecruitmentListData } from './types';
import GridContainer from '@/components/common/elements/GridContainer';
import StyledBox from '@/components/common/elements/Box';
import Text from '@/components/common/elements/Text';
import React, { useState, memo, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { CONTENT_PADDING_X } from '@/styles/layout';
import { useIsMobile } from '@/common/hooks';
import { SummaryLine, WorkDaysLine, keepTildeTogether, summaryText, workDaysLineHeight, summaryLineHeight } from './recruitmentSummary';
import OneLineTags from './OneLineTags';
import { FORM_COLOR } from '@/pages/Seminar/components/form/formStyles';
import { EyeIcon, HeartIcon } from '@/components/common/elements/CountIcons';

const CountIcons = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-size: 9px;
    line-height: 1.4;
    & > span { display: inline-flex; align-items: center; gap: 2px; }
    [data-count-value] {
        font-size: inherit;
        font-weight: 500;
        font-variant-numeric: tabular-nums;
        letter-spacing: 0;
        color: #6b7280;
        /* HOT·전문가와 같은 중앙 정렬에 숫자 획의 광학 중심만 보정한다. */
        transform: translateY(0.25px);
    }
    svg {
        display: block;
        width: 9px;
        height: 9px;
        flex-shrink: 0;
        stroke: currentColor;
        fill: none;
        stroke-width: 1.8;
        stroke-linecap: round;
        stroke-linejoin: round;
    }
`;

const RecruitmentCounts = ({ views = 0, favorites = 0, icons = false }: { views?: number; favorites?: number; icons?: boolean }) => icons ? (
    <CountIcons>
        <span aria-label={`조회 ${views.toLocaleString()}`}><EyeIcon /><span data-count-value>{views.toLocaleString()}</span></span>
        <span aria-label={`관심 ${favorites.toLocaleString()}`}><HeartIcon /><span data-count-value>{favorites.toLocaleString()}</span></span>
    </CountIcons>
) : <>조회 {views.toLocaleString()} · 관심 {favorites.toLocaleString()}</>;

// Separate memoized image component to prevent unnecessary re-renders
export const RecruitmentImage = memo(({ thumbnail, title }: { thumbnail?: string; title: string }) => {
    const [imageError, setImageError] = useState(false);

    if (!thumbnail || imageError) {
        return (
            <div
                data-card-part="recruitment-image"
                style={{
                    width: '100%',
                    aspectRatio: '16 / 9',
                    background: '#f5f6f8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    color: '#999',
                    textAlign: 'center',
                    padding: '4px'
                }}
            >
                이미지<br/>준비중
            </div>
        );
    }

    return (
        <div data-card-part="recruitment-image" style={{ width: '100%', aspectRatio: '16 / 9', overflow: 'hidden', background: '#f5f6f8' }}>
            <img
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
                alt={title}
                src={thumbnail}
                loading="lazy"
                onError={() => setImageError(true)}
            />
        </div>
    );
});

RecruitmentImage.displayName = 'RecruitmentImage';

// 채용 카드 배지 — 썸네일 좌상단에 **하나만**(2026-10-01 사용자 결정). 여러 조건에 해당하면 우선순위가 높은 것 하나:
//   모집종료 → 급구(빨강) → 종료임박(D-3) → 신규(등록 24h내) → 상시모집
// 급구로 등록한 공고는 '신규' 대신 '급구'를 보여 주고, 등록한 지 24시간이 지나도 계속 나온다.
export type RecruitmentBadgeInfo = { continuousRecruitment?: boolean; createAt?: string; publishedAt?: string | null; endDate?: string | null; isExpired?: boolean; emergency?: boolean };
export type BadgeStyle = { label: string; bg: string; color: string };
/** 급구 배지 — 빨강 바탕 흰 글자. 빨강은 UI 가이드의 한 가지(#E11D48)만 쓴다 */
export const URGENT_BADGE: BadgeStyle = { label: '급구', bg: FORM_COLOR.danger, color: '#fff' };
const DAY_MS: number = 24 * 60 * 60 * 1000;
/** 보여 줄 배지 — 없거나 하나(배열은 그리는 쪽 호환용) */
export const getRecruitmentBadges = (r: RecruitmentBadgeInfo): BadgeStyle[] => {
    const now = Date.now();
    const expired = r.isExpired || (!r.continuousRecruitment && r.endDate ? new Date(r.endDate).getTime() < now : false);
    if (expired) return [{ label: '모집종료', bg: '#eceef1', color: '#6b7280' }];
    if (r.emergency) return [URGENT_BADGE];
    if (!r.continuousRecruitment && r.endDate) {
        const daysLeft = (new Date(r.endDate).getTime() - now) / DAY_MS;
        if (daysLeft >= 0 && daysLeft <= 3) return [{ label: '종료임박', bg: '#ffe4e6', color: '#e11d48' }];
    }
    // 신규 = 공고 등록 후 24시간(2026-10-01 사용자 확인) — 상시모집 공고도 등록 24시간 안이면 '신규'가 먼저.
    // 기준은 등록(게시) 시각 — 임시저장했다가 나중에 등록한 공고도 등록한 때부터 센다. 옛 응답(값 없음)만 저장 시각으로 본다
    const registeredAt: string | null | undefined = r.publishedAt ?? r.createAt;
    if (registeredAt && now - new Date(registeredAt).getTime() < DAY_MS) {
        return [{ label: '신규', bg: '#dcfce7', color: '#16a34a' }];
    }
    if (r.continuousRecruitment) return [{ label: '상시모집', bg: '#e7f3f0', color: '#28766f' }];
    return [];
};

/** 썸네일 배지 줄 — 좌상단. 모바일은 썸네일(146×112)이 작아 배지를 조금 줄인다(2026-10-01 사용자 지시: 사진이 가려짐) */
const ThumbBadgeBox = styled.div`
    position: absolute;
    top: 6px;
    left: 6px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    z-index: 2;

    @media (max-width: 768px) {
        top: 5px;
        left: 5px;
    }
`;

const ThumbBadge = styled.span`
    display: inline-block;
    width: fit-content;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;

    @media (max-width: 768px) {
        padding: 1px 4px;
        border-radius: 3px;
        font-size: 10px;
    }
`;

export const RecruitmentBadges = ({ info }: { info: RecruitmentBadgeInfo; isMobile?: boolean }) => {
    const badges = getRecruitmentBadges(info);
    if (badges.length === 0) return null;
    return (
        <ThumbBadgeBox>
            {badges.map((b, i) => (
                <ThumbBadge key={i} data-recruitment-badge style={{ background: b.bg, color: b.color }}>
                    {b.label}
                </ThumbBadge>
            ))}
        </ThumbBadgeBox>
    );
};

// 내 관심치과 공고 — 썸네일 우하단 배지 (PC: 칩, 모바일: 하트만·배경 없음)
export const FavoriteClinicBadge = ({ show, isMobile }: { show?: boolean; isMobile?: boolean }) => {
    if (!show) return null;
    if (isMobile) {
        return (
            <div style={{ position: 'absolute', bottom: 6, right: 8, zIndex: 2, lineHeight: 1 }}>
                <span style={{ fontSize: '1.15rem', color: '#ff5fa2', filter: 'drop-shadow(0 0 1.5px rgba(0,0,0,0.5))' }}>
                    ♥
                </span>
            </div>
        );
    }
    return (
        <div style={{ position: 'absolute', bottom: 6, right: 6, zIndex: 2 }}>
            <span style={{ display: 'inline-block', background: '#ffe4ef', color: '#db2777', fontSize: '12px', fontWeight: 700, padding: '2px 6px', borderRadius: 4, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                ♥ 관심치과
            </span>
        </div>
    );
};

/**
 * 카드 제목 — 어절 단위 줄바꿈(keep-all) + 짧은 마지막 줄 피하기(text-wrap: pretty, 지원 안 하는 브라우저는 그냥 줄바꿈).
 * 한 어절이 한 줄보다 길 때만 어절 안에서 쪼갠다(overflow-wrap: anywhere).
 */
const CardTitle = styled(Text)`
    word-break: keep-all;
    overflow-wrap: anywhere;
    text-wrap: pretty;
`;

/**
 * 제목 묶음(병원명·제목)과 조건 묶음(근무 요일·요약·복지) 사이 간격 — 고정(2026-10-01 사용자 결정, 세미나 카드와 같은 방식).
 * 제목은 최대 두 줄이고 한 줄일 때 빈 줄을 예약하지 않는다. 남는 높이는 카드 맨 아래로 간다(위아래로 벌리지 않는다).
 */
const PC_CARD_GROUP_GAP: string = '4px';
const MOBILE_CARD_GROUP_GAP: string = '5px';
/** 모바일 글 칸 높이 = 썸네일 높이. 가장 긴 경우(제목 두 줄 + 근무 요일 + 요약 + 복지)가 정확히 들어간다 */
const MOBILE_CARD_CONTENT_HEIGHT = '112px';
/** 프리미엄 본문은 제목 두 줄까지 들어가는 높이만 고정한다. 한 줄 제목 아래 정보는 바로 이어지고 남는 공간은 하단에 둔다. */
const premiumMobileTextHeight = (fullWidth?: boolean): string =>
    `calc(${fullWidth ? 30 : 22}px + 14px + 3px + ${fullWidth ? 48 : 32}px + ${MOBILE_CARD_GROUP_GAP} + 14px + 6px + 12px + 3px + ${RECRUITMENT_CHIP_HEIGHT + 6}px)`;

// 복지 타입별 이모지 (복지 칩 표기용 — recruitment_components 라벨과 동일)
const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
  height: 100%;

  @media (max-width: 768px) {
    .recruitment-card {
      height: auto !important;
      padding: 1rem !important;
      border: 1px solid #e5e7eb;
      border-radius: 0.75rem;
    }
  }
`;

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

/** PC 카드 글 칸 고정 높이 — 안쪽 여백 20 + 윗줄 20 + 줄 사이 4×4 + 제목 두 줄 + 근무 요일 + 요약 + 칩 줄(12 + 위 여백 2) */
export const PC_CARD_TEXT_FIXED_HEIGHT: string = `calc(20px + 20px + 16px + 0.95rem * 2.6 + ${workDaysLineHeight(true)} + ${summaryLineHeight()} + ${RECRUITMENT_CHIP_HEIGHT + 2}px)`;

export interface RecruitmentCardTextProps {
    clinic: StationClinic & { name: string; city: string; district: string };
    title: string;
    jobTasks: string[];
    recruitmentTypes: string[];
    welfares?: { description: string; type?: string }[];
    viewCount?: number;
    favoriteCount?: number;
    workDaysLabel?: string;
    infoChips?: string[];
    /** 당일 지급 공고 — 칩 줄은 복지 칩 대신 역 → 당일 지급 → 급여 */
    sameDayPay?: boolean;
    /** 카드 급여 칩(서버) — 칩 줄은 역 → 급여 → 복지 */
    payChip?: string | null;
    /** 글 길이와 무관하게 높이 고정(홈 프리미엄). 기본은 내용만큼 — 같은 줄 카드 높이는 그리드가 늘려 맞춘다 */
    fixedHeight?: boolean;
    iconCounts?: boolean;
}

/**
 * PC 채용 카드 글 칸 — 병원명·조회 → 제목(최대 두 줄) → 근무 요일 → 요약 → 칩.
 * 채용 목록·홈 추천(RecruitmentCard)과 홈 프리미엄(CardListCarousel)이 같은 부품을 써서 글자 크기·간격이 같다(2026-10-01 사용자 지시).
 * 묶음 사이 간격은 고정이고 남는 높이는 아래로 간다.
 */
export const RecruitmentCardText = ({ clinic, title, jobTasks, recruitmentTypes, welfares, viewCount, favoriteCount, workDaysLabel, infoChips, sameDayPay, payChip, fixedHeight = false, iconCounts = false }: RecruitmentCardTextProps) => {
    const chips: string[] = recruitmentChips(clinic, welfares, infoChips, sameDayPay, payChip);
    return (
        <div data-card-part="pc-text" style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: PC_CARD_GROUP_GAP, minWidth: 0, overflow: 'hidden', boxSizing: 'border-box', ...(fixedHeight ? { height: PC_CARD_TEXT_FIXED_HEIGHT, flexShrink: 0 } : { flex: 1 }) }}>
            <div data-card-group="heading" style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px', height: '20px', flexShrink: 0 }}>
                    <Text size='0.8rem' textcolor='grey' style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                        {clinic.name}
                    </Text>
                    <Text size='12px' textcolor='grey' style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                        <RecruitmentCounts views={viewCount} favorites={favoriteCount} icons={iconCounts} />
                    </Text>
                </div>
                <CardTitle size='0.95rem' fontweight='bold' style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: '1.3',
                    flexShrink: 0,
                }}>
                    {keepTildeTogether(title)}
                </CardTitle>
            </div>
            <div data-card-group="conditions" style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                {workDaysLabel?.trim() && <WorkDaysLine pc label={workDaysLabel.trim()} />}
                <SummaryLine pc text={summaryText(clinic.city, clinic.district, jobTasks, recruitmentTypes, true)} />
                {chips.length > 0 && (
                    <OneLineTags
                        heightPx={RECRUITMENT_CHIP_HEIGHT}
                        tags={chips}
                        keepFirst={!!stationChip(clinic)}
                        pinned={pinnedChipsOf(sameDayPay, payChip, infoChips)}
                        gapPx={4}
                        style={recruitmentChipRowStyle}
                        tagStyle={recruitmentChipStyle}
                        accentTag={accentChipOf(sameDayPay)}
                        accentStyle={recruitmentChipAccentStyle}
                        plusStyle={recruitmentChipCountStyle}
                    />
                )}
            </div>
        </div>
    );
};

// Desktop card component
export const RecruitmentCard = memo(({ id, clinic, title, jobTasks, recruitmentTypes, welfares, createAt, publishedAt, endDate, continuousRecruitment, isExpired, isFavoriteClinic, isMobile, viewCount, favoriteCount, workDaysLabel, emergency, infoChips, sameDayPay, payChip }: {
    id: number;
    clinic: StationClinic & { pcThumbnail?: string; mobileThumbnail?: string; name: string; city: string; district: string };
    title: string;
    jobTasks: string[];
    recruitmentTypes: string[];
    position?: string;
    welfares?: { description: string; type?: string }[];
    createAt?: string;
    publishedAt?: string | null;
    endDate?: string | null;
    continuousRecruitment?: boolean;
    isExpired?: boolean;
    isFavoriteClinic?: boolean;
    isMobile: boolean;
    viewCount?: number;
    favoriteCount?: number;
    workDaysLabel?: string;
    emergency?: boolean;
    infoChips?: string[];
    sameDayPay?: boolean;
    payChip?: string | null;
}) => {
    // 모바일이면 mobileThumbnail, PC면 pcThumbnail 사용
    const displayImage = isMobile ? clinic.mobileThumbnail : clinic.pcThumbnail;

    return (
        <Grid item key={id} xs={12} sm={6} md={3}>
            <StyledLink to={`/recruitments/${id}`}>
                <div data-card="grid-pc" {...uiTargetAttrs(UI_KIND.RECRUITMENT, id)} style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    background: '#fff',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    <div style={{ position: 'relative' }}>
                        <RecruitmentImage thumbnail={displayImage} title={title} />
                        <RecruitmentBadges info={{ createAt, publishedAt, endDate, continuousRecruitment, isExpired, emergency }} isMobile={isMobile} />
                        <FavoriteClinicBadge show={isFavoriteClinic} isMobile={isMobile} />
                    </div>
                    <RecruitmentCardText clinic={clinic} title={title} jobTasks={jobTasks} recruitmentTypes={recruitmentTypes} welfares={welfares} viewCount={viewCount} favoriteCount={favoriteCount} workDaysLabel={workDaysLabel} infoChips={infoChips} sameDayPay={sameDayPay} payChip={payChip} />
                </div>
            </StyledLink>
        </Grid>
    );
});

RecruitmentCard.displayName = 'RecruitmentCard';

const MobileWrapper = styled.div<{ isListLayout?: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: relative;
    width: ${({ isListLayout }) => isListLayout ? '100%' : 'calc(100% + 1rem)'};
    margin: ${({ isListLayout }) => isListLayout ? '0' : '0 -0.5rem'};
    padding-top: 0;
  }
`;

const MobileSwipeContainer = styled.div`
  @media (max-width: 768px) {
    display: flex;
    /* 높이는 멈춘 페이지 높이(인라인 height) — 카드가 적은 페이지 아래가 비지 않게. 바뀔 때만 짧게 */
    align-items: flex-start;
    transition: height 0.2s ease;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  @media (max-width: 768px) and (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const MobilePage = styled.div`
  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
    scroll-snap-align: center;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0 0.5rem;
    box-sizing: border-box;
    flex-shrink: 0;
    /* 가로 넘김 줄(flex)의 기본 stretch 로 가장 높은 페이지 높이까지 늘어나지 않게 — 카드가 적은 마지막 페이지는 카드만큼만 */
    align-self: flex-start;
  }
`;

const MobileCard = styled(StyledLink)`
  display: flex;
  flex-direction: row;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  overflow: hidden;
  padding: 0;
  gap: 0;
  align-items: stretch;
  box-shadow: none;
  /* 높이는 글 칸 고정 높이(MOBILE_CARD_CONTENT_HEIGHT)로 정해진다 — 내용 길이와 무관, 썸네일은 그 높이에 맞춰 채운다.
     기반 StyledLink 의 height: 100% 는 끊는다(부모 높이를 따라 늘어나지 않게), 가로 넘김 페이지 안에서도 줄지 않게 */
  height: auto;
  flex-shrink: 0;
`;

/** 미리보기 카드 — 목록 카드와 같은 모양의 링크 없는 상자(AI 대화의 공고 미리보기·다시 올리기 고르기 카드) */
const MobilePreviewCard = MobileCard.withComponent('div');

const MobileCardImage = styled.div`
  position: relative;

  /* 기존 썸네일 크기를 유지한다. 글 칸이 높아져도 사진 비율은 바꾸지 않는다. */
  width: 146px;
  height: 112px;
  align-self: stretch;
  flex-shrink: 0;
  overflow: hidden;

  & > div:first-of-type {
    position: absolute;
    inset: 0;
  }

  img, & > div:first-of-type {
    height: 100% !important;
    width: 146px !important;
    aspect-ratio: auto !important;
    object-fit: cover !important;
    background: #f9fafb;
  }
`;

const MobileCardContent = styled.div<{ stacked?: boolean; fullWidth?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${MOBILE_CARD_GROUP_GAP};
  min-width: 0;
  padding: ${props => props.stacked ? (props.fullWidth ? '14px 16px 16px' : '10px 12px 12px') : '7px 10px'};
  box-sizing: border-box;
  height: ${props => props.stacked ? premiumMobileTextHeight(props.fullWidth) : MOBILE_CARD_CONTENT_HEIGHT};

  [data-line] {
    height: 14px;
    line-height: 14px;
  }

  [data-line="summary"] {
    height: 12px;
    line-height: 12px;
  }
`;

const MobileCardGroup = styled.div<{ premiumConditions?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex-shrink: 0;
  ${props => props.premiumConditions && `
    [data-line="workdays"] { margin-bottom: 3px; }
  `}
`;

/** 추천·프리미엄의 정보는 같은 부품을 쓰고 프리미엄만 세로 카드의 본문 높이·여백을 적용한다. */
export const MobileRecruitmentCardText = ({ recruitment, stacked = false, fullWidth = false, allowWelfareOverflow = false, clinicNameSize = '12px', hideCounts = false }: { recruitment: RecruitmentListData; stacked?: boolean; fullWidth?: boolean; allowWelfareOverflow?: boolean; clinicNameSize?: string; /** 조회·관심 숨김 — 예시 카드(가입 완료 화면) */ hideCounts?: boolean }) => {
    const titleRef = useRef<HTMLDivElement>(null);
    const [singleLineTitle, setSingleLineTitle] = useState(false);

    useLayoutEffect(() => {
        if (!allowWelfareOverflow) return undefined;
        const title = titleRef.current;
        if (!title) return undefined;
        let active = true;
        const measure = (): void => {
            if (!active) return;
            const height = title.getBoundingClientRect().height;
            const lineHeight = parseFloat(getComputedStyle(title).lineHeight);
            setSingleLineTitle(height > 0 && height <= lineHeight + 1);
        };
        measure();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        observer?.observe(title);
        document.fonts?.ready.then(measure);
        return () => { active = false; observer?.disconnect(); };
    }, [recruitment.title, fullWidth, allowWelfareOverflow]);

    return (
            <MobileCardContent stacked={stacked} fullWidth={fullWidth} data-card-part="mobile-text">
                <MobileCardGroup data-card-group="heading">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px', height: '14px', lineHeight: '14px', flexShrink: 0 }}>
                    <Text size={stacked ? '11px' : clinicNameSize} textcolor='grey' style={{ lineHeight: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                        {recruitment.clinic.name}
                    </Text>
                    {!hideCounts && (
                        <Text size='10px' textcolor='grey' style={{ display: 'inline-flex', alignItems: 'center', height: '14px', lineHeight: '14px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            <RecruitmentCounts views={recruitment.viewCount} favorites={recruitment.favoriteCount} icons={stacked} />
                        </Text>
                    )}
                </div>
                {/* A안: 병원명과 제목은 위에, 조건과 복지는 아래에 모은다. */}
                <CardTitle ref={titleRef} size={fullWidth ? '18px' : '13px'} fontweight='bold' style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: fullWidth ? '24px' : '16px',
                    flexShrink: 0,
                }}>
                    {keepTildeTogether(recruitment.title)}
                </CardTitle>
                </MobileCardGroup>
                <MobileCardGroup data-card-group="conditions" premiumConditions={stacked}>
                {recruitment.workDaysLabel?.trim() && <WorkDaysLine label={recruitment.workDaysLabel.trim()} />}
                <SummaryLine size="10px" text={summaryText(recruitment.clinic.city, recruitment.clinic.district, recruitment.jobTasks, recruitment.recruitmentTypes, false)} />
                {recruitmentChips(recruitment.clinic, recruitment.welfares, recruitment.infoChips, recruitment.sameDayPay, recruitment.payChip).length > 0 && (
                    <OneLineTags
                            heightPx={stacked ? RECRUITMENT_CHIP_HEIGHT + 4 : RECRUITMENT_CHIP_HEIGHT}
                        tags={recruitmentChips(recruitment.clinic, recruitment.welfares, recruitment.infoChips, recruitment.sameDayPay, recruitment.payChip)}
                        keepFirst={!!stationChip(recruitment.clinic)}
                        pinned={pinnedChipsOf(recruitment.sameDayPay, recruitment.payChip, recruitment.infoChips)}
                        max={3}
                        gapPx={3}
                        style={recruitmentChipRowStyle}
                        tagStyle={stacked ? { ...recruitmentChipStyle, padding: '2px 3px' } : recruitmentChipStyle}
                        accentTag={accentChipOf(recruitment.sameDayPay)}
                        accentStyle={recruitmentChipAccentStyle}
                        plusStyle={recruitmentChipCountStyle}
                        overflowTags={allowWelfareOverflow && singleLineTitle ? recruitmentChips(recruitment.clinic, recruitment.welfares, recruitment.infoChips, recruitment.sameDayPay, recruitment.payChip) : undefined}
                    />
                )}
            </MobileCardGroup>
            </MobileCardContent>
    );
};

// 모바일 채용 카드 — 목록(list)/홈 추천(carousel) 공용 단일 컴포넌트 (통일)
/** preview: 아직 없는 공고의 미리보기(AI 대화의 공고 글 옮기기) — 같은 카드를 링크 없이 그린다 */
export const MobileRecruitmentCard = memo(({ recruitment, isMobile, allowWelfareOverflow = false, clinicNameSize, preview = false, hideCounts = false }: { recruitment: RecruitmentListData; isMobile: boolean; allowWelfareOverflow?: boolean; clinicNameSize?: string; preview?: boolean; hideCounts?: boolean }) => {
    const displayImage = isMobile ? recruitment.clinic.mobileThumbnail : recruitment.clinic.pcThumbnail;
    const body = (
        <>
            <MobileCardImage>
                <RecruitmentImage thumbnail={displayImage} title={recruitment.title} />
                <RecruitmentBadges info={recruitment} isMobile={isMobile} />
                <FavoriteClinicBadge show={recruitment.isFavoriteClinic} isMobile={isMobile} />
            </MobileCardImage>
            <MobileRecruitmentCardText recruitment={recruitment} allowWelfareOverflow={allowWelfareOverflow} clinicNameSize={clinicNameSize} hideCounts={hideCounts} />
        </>
    );

    return (
        <>
            {/* 미리보기는 링크가 아닌 상자 — 예전엔 as='div' 가 Link 감싸개에 전달되지 않아 <a href="/"> 로 그려져 누르면 홈으로 갔다(2026-10-12 확인) */}
            {preview ? (
                <MobilePreviewCard to="" data-card="preview-mobile">{body}</MobilePreviewCard>
            ) : (
                <MobileCard to={`/recruitments/${recruitment.id}`} data-card="grid-mobile" {...uiTargetAttrs(UI_KIND.RECRUITMENT, recruitment.id)}>{body}</MobileCard>
            )}
        </>
    );
});

MobileRecruitmentCard.displayName = 'MobileRecruitmentCard';

const PageIndicator = styled.div`
  @media (max-width: 768px) {
    position: absolute;
    top: -2rem;
    right: 0;
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
  }
`;

const DesktopGridContainer = styled(GridContainer)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileListContainer = styled.div`
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.75rem 0;
  }
`;

/** 스크롤이 이 시간 동안 멈춰 있으면 "멈췄다"로 본다(scrollend 를 지원하지 않는 브라우저용) */
const SCROLL_SETTLE_MS: number = 120;

const RecruitmentGrid = ({ data, isPremium = false, layout = 'carousel', onPageChange }: RecruitmentGridProps) => {
    const [currentPage, setCurrentPage] = useState(1);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const isMobile = useIsMobile();
    /** 가로 넘김 칸 높이 — 스크롤이 멈춘 페이지의 높이. 모르면 undefined(가장 높은 페이지 높이) */
    const [swipeHeight, setSwipeHeight] = useState<number | undefined>(undefined);
    /** 손가락이 닿아 있는 동안은 높이를 바꾸지 않는다(스와이프 중 흔들림 방지) */
    const touchingRef = useRef<boolean>(false);
    const settleTimerRef = useRef<number | undefined>(undefined);

    // 모바일용: 4개씩 페이지로 나누기 (빈칸은 채우지 않음)
    const mobilePages = [];
    if (data && layout === 'carousel') {
        for (let i = 0; i < data.length; i += 4) {
            mobilePages.push(data.slice(i, i + 4));
        }
    }

    /** 멈춘 페이지의 높이를 재서 넘김 칸 높이로 — 페이지 경계에 딱 맞게 멈췄을 때만(스냅 도중이면 다음 기회에) */
    const measureSettledPage = useCallback((): void => {
        const container: HTMLDivElement | null = containerRef.current;
        if (!container || touchingRef.current) return;
        const pageWidth: number = container.clientWidth;
        const pages: HTMLCollection = container.children;
        if (pageWidth === 0 || pages.length === 0) return;
        const index: number = Math.min(Math.max(Math.round(container.scrollLeft / pageWidth), 0), pages.length - 1);
        if (Math.abs(container.scrollLeft - index * pageWidth) > 2) return;
        const height: number = (pages[index] as HTMLElement).offsetHeight;
        setSwipeHeight(height > 0 ? height : undefined);
    }, []);

    const scheduleMeasure = useCallback((): void => {
        window.clearTimeout(settleTimerRef.current);
        settleTimerRef.current = window.setTimeout(measureSettledPage, SCROLL_SETTLE_MS);
    }, [measureSettledPage]);

    // 창 크기·데이터·이미지로 페이지 높이가 바뀌면 다시 잰다 + 손가락 떼기·scrollend 에서 잰다
    const pageCount: number = mobilePages.length;
    useEffect(() => {
        const container: HTMLDivElement | null = containerRef.current;
        if (layout !== 'carousel' || !container) return undefined;
        const observer: ResizeObserver = new ResizeObserver(() => scheduleMeasure());
        Array.from(container.children).forEach((page: Element) => observer.observe(page));
        const onTouchStart = (): void => {
            touchingRef.current = true;
            window.clearTimeout(settleTimerRef.current);
        };
        const onTouchEnd = (): void => {
            touchingRef.current = false;
            scheduleMeasure();
        };
        const onScrollEnd = (): void => {
            if (touchingRef.current) return;
            window.clearTimeout(settleTimerRef.current);
            measureSettledPage();
        };
        container.addEventListener('touchstart', onTouchStart, { passive: true });
        container.addEventListener('touchend', onTouchEnd, { passive: true });
        container.addEventListener('touchcancel', onTouchEnd, { passive: true });
        container.addEventListener('scrollend', onScrollEnd);
        measureSettledPage();
        return () => {
            observer.disconnect();
            container.removeEventListener('touchstart', onTouchStart);
            container.removeEventListener('touchend', onTouchEnd);
            container.removeEventListener('touchcancel', onTouchEnd);
            container.removeEventListener('scrollend', onScrollEnd);
            window.clearTimeout(settleTimerRef.current);
        };
    }, [layout, data, pageCount, measureSettledPage, scheduleMeasure]);

    // 스크롤 이벤트로 현재 페이지 추적
    const handleScroll = () => {
        if (containerRef.current) {
            const scrollLeft = containerRef.current.scrollLeft;
            const pageWidth = containerRef.current.offsetWidth;
            const page = Math.round(scrollLeft / pageWidth) + 1;
            setCurrentPage(page);
            if (onPageChange) {
                onPageChange(page);
            }
        }
        // 멈춘 뒤에만 높이를 바꾼다(scrollend 가 없는 브라우저는 잠깐 조용하면 멈춘 것으로)
        scheduleMeasure();
    };

    return (
        <>
            {/* Desktop */}
            <DesktopGridContainer>
                {data?.map((recruitment) => (
                    <RecruitmentCard key={recruitment.id} {...recruitment} isMobile={isMobile} />
                ))}
            </DesktopGridContainer>

            {/* Mobile - List Layout */}
            {layout === 'list' && (
                <MobileWrapper isListLayout={true}>
                    <MobileListContainer data-recruitment-mobile-list>
                        {data?.map((recruitment) => (
                            <MobileRecruitmentCard key={recruitment.id} recruitment={recruitment} isMobile={isMobile} allowWelfareOverflow />
                        ))}
                    </MobileListContainer>
                </MobileWrapper>
            )}

            {/* Mobile - Carousel Layout */}
            {layout === 'carousel' && (
                <MobileWrapper isListLayout={false}>
                    <MobileSwipeContainer ref={containerRef} onScroll={handleScroll} style={isMobile && swipeHeight !== undefined ? { height: swipeHeight } : undefined}>
                        {mobilePages.map((pageData, pageIndex) => (
                            <MobilePage key={pageIndex}>
                                {pageData.map((recruitment) => (
                                    <MobileRecruitmentCard key={recruitment.id} recruitment={recruitment} isMobile={isMobile} allowWelfareOverflow clinicNameSize="10px" />
                                ))}
                            </MobilePage>
                        ))}
                    </MobileSwipeContainer>
                </MobileWrapper>
            )}
        </>
    );
};

export default RecruitmentGrid;
